import React from 'react';
import { arrayOf, bool, func, node, number, object, oneOf, string, shape } from 'prop-types';
import classNames from 'classnames';
import { NamedLink, Pill } from '../../components';

import css from './TabNavHorizontal.module.css';

export const LIGHT_SKIN = 'light';
export const DARK_SKIN = 'dark';

/**
 * A tab's label, with a count after it ("Episodes 12", the board's small neutral pill) or a dot
 * (something new; `dotLabel` says what to screen readers). TabNav uses it too.
 */
export const TabLabel = props => {
  const { text, count = null, dot = false, dotLabel = null } = props;
  return (
    <>
      {text}
      {count !== null && count !== undefined ? <Pill size="small">{count}</Pill> : null}
      {dot ? <span className={css.dot} aria-hidden="true" /> : null}
      {dot && dotLabel ? <span className={css.srOnly}>{dotLabel}</span> : null}
    </>
  );
};

// In-page tabs move with the arrow keys, Home and End, and select as they move (WAI-ARIA tabs).
const MOVES = { ArrowRight: 1, ArrowLeft: -1 };
export const handleTabListKeyDown = event => {
  const tabs = Array.from(event.currentTarget.querySelectorAll('[role="tab"]:not(:disabled)'));
  const current = tabs.indexOf(event.target);
  if (current < 0) return;
  let next = null;
  if (event.key in MOVES) next = (current + MOVES[event.key] + tabs.length) % tabs.length;
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = tabs.length - 1;
  if (next === null) return;
  event.preventDefault();
  tabs[next].focus();
  tabs[next].click();
};

const Tab = props => {
  const {
    className,
    tabContentClassName,
    selectedTabContentClassName,
    id,
    panelId,
    disabled = false,
    text,
    count,
    dot,
    dotLabel,
    selected = false,
    onClick,
    linkProps,
    isDark,
  } = props;
  const classes = classNames(
    css.tabContent,
    tabContentClassName,
    {
      [css.selectedTabContent]: selected,
      [css.disabled]: disabled,
      [css.tabContentDarkSkin]: isDark,
      [css.selectedTabContentDarkSkin]: isDark && selected,
    },
    selected ? selectedTabContentClassName : null
  );
  const label = <TabLabel text={text} count={count} dot={dot} dotLabel={dotLabel} />;

  if (onClick) {
    return (
      <div className={className} role="presentation">
        <button
          type="button"
          role="tab"
          id={id}
          aria-selected={selected}
          aria-controls={panelId}
          tabIndex={selected ? 0 : -1}
          disabled={disabled}
          className={classes}
          onClick={onClick}
        >
          {label}
        </button>
      </div>
    );
  }
  return (
    <div className={className}>
      <NamedLink className={classes} {...linkProps}>
        {label}
      </NamedLink>
    </div>
  );
};

const TabNavHorizontal = props => {
  const {
    className,
    rootClassName,
    tabRootClassName,
    tabContentClassName,
    selectedTabContentClassName,
    tabs,
    skin = LIGHT_SKIN,
    ariaLabel,
    isTabList = false,
  } = props;
  const isDark = skin === DARK_SKIN;
  const classes = classNames(rootClassName || css.root, { [css.darkSkin]: isDark }, className);
  const tabClasses = tabRootClassName || css.tab;
  const items = tabs.map((tab, index) => {
    const key = tab.id || (typeof tab.text === 'string' ? tab.text : index);
    return (
      <Tab
        key={key}
        className={tabClasses}
        tabContentClassName={tabContentClassName}
        selectedTabContentClassName={selectedTabContentClassName}
        {...tab}
        isDark={isDark}
      />
    );
  });

  return isTabList ? (
    <div className={classes} role="tablist" aria-label={ariaLabel} onKeyDown={handleTabListKeyDown}>
      {items}
    </div>
  ) : (
    <nav className={classes} aria-label={ariaLabel}>
      {items}
    </nav>
  );
};

const tabShape = {
  text: node.isRequired,
  // A count after the label ("Episodes 12"), or a dot for something new
  count: number,
  dot: bool,
  dotLabel: string,
  disabled: bool,
  selected: bool,
};

const sharedPropTypes = {
  className: string,
  rootClassName: string,
  tabRootClassName: string,
  tabContentClassName: string,
  selectedTabContentClassName: string,
  skin: oneOf([LIGHT_SKIN, DARK_SKIN]),
  ariaLabel: string,
};

/**
 * In-page tabs (a tablist of buttons). Each tab has `onClick`, and `id` and `panelId` to tie it
 * to its panel: the panel is `<div role="tabpanel" id={panelId} aria-labelledby={id}>`.
 */
export const ButtonTabNavHorizontal = props => <TabNavHorizontal {...props} isTabList />;

ButtonTabNavHorizontal.propTypes = {
  ...sharedPropTypes,
  tabs: arrayOf(
    shape({ ...tabShape, id: string.isRequired, panelId: string, onClick: func.isRequired })
  ).isRequired,
};

/**
 * The panel of one in-page tab. Only the selected panel renders.
 */
export const TabPanel = props => {
  const { className, id, tabId, selected = false, children } = props;
  return selected ? (
    <div className={className} role="tabpanel" id={id} aria-labelledby={tabId}>
      {children}
    </div>
  ) : null;
};

TabPanel.propTypes = {
  className: string,
  // The panel's id (the tab's panelId) and the id of the tab that names it
  id: string.isRequired,
  tabId: string.isRequired,
  selected: bool,
  children: node,
};

/**
 * A tab navigation element with links. Requires linkProps
 * object param for tab objects passed as parameter.
 */
export const LinkTabNavHorizontal = props => <TabNavHorizontal {...props} />;

LinkTabNavHorizontal.propTypes = {
  ...sharedPropTypes,
  tabs: arrayOf(shape({ ...tabShape, linkProps: object.isRequired })).isRequired,
};
